/**
 * Pure column-visibility predicate for the
 * `Hide_All_Passing_Toggle` on the Validation Step Breakdown pivot.
 *
 * Free of React, Redux, and DOM references; the predicate is a
 * function of `(column, visibleRows)` only and does not inspect
 * hidden descendants of collapsed groups.
 */

import { pivotCellKey, type PivotColumn, type PivotRow } from "./stepBreakdown";

/**
 * Returns `true` iff every row in `visibleRows` has a `PivotCell`
 * for `column.stepName` whose `totalCount > 0` and whose raw
 * `passRate === 100`.
 *
 * Rows without a cell for this column's step name are treated as
 * failing the all-passing test, so a missing cell on any visible
 * row keeps the column visible. Zero-total cells are not treated
 * as passing: a column whose only visible cells are zero-total
 * stays visible. An empty `visibleRows` array returns `false` —
 * with nothing to evaluate, the toggle has no basis to hide the
 * column.
 */
export function shouldHideColumn(
  column: PivotColumn,
  visibleRows: readonly PivotRow[]
): boolean {
  if (visibleRows.length === 0) {
    return false;
  }
  const columnKey = pivotCellKey(column);
  for (const row of visibleRows) {
    const cell = row.cellsByColumnKey.get(columnKey);
    if (cell === undefined) {
      return false;
    }
    if (cell.totalCount <= 0 || cell.passRate !== 100) {
      return false;
    }
  }
  return true;
}
